import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { AlertController, LoadingController, ModalController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { environment } from '@env/environment';
import { Logger, UntilDestroy } from '@shared';
import { AuthService } from '@app/@shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { RecoveryComponent } from './recovery/recovery.component';
import { TrackingService } from '@app/@shared/services/tracking.service';
import { BlackUser } from '@app/@shared/models/customer.model';
import { CustomerService } from '@app/@shared/services/customer.service';
import { IpApiService } from '@app/@shared/services/ip-api/ip-api.service';
import { LocationService } from '@app/@shared/services/location.service';
import { NotificationsService } from '@app/@shared/services/notifications.service';

const log = new Logger('login-component');

@UntilDestroy()
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  version: string | null = environment.version;
  error: string | undefined;
  loginForm!: UntypedFormGroup;
  isLoading = false;
  subs: Subscription[] = [];
  blackUser: BlackUser[] = [];
  isAllowed = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private platform: Platform,
    private loadingController: LoadingController,
    private authService: AuthService,
    private translateService: TranslateService,
    private modalCtrl: ModalController,
    private trackingS: TrackingService,
    private alertCtrl: AlertController,
    private customerS: CustomerService,
    private ipApiService: IpApiService,
    private notificationsS: NotificationsService,
    private locationS: LocationService
  ) {
    this.createForm();
  }

  ngOnInit() {
    this.updateAllowed();
  }

  ionViewDidLeave() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  private createForm() {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      remember: true,
    });
  }

  get isWeb(): boolean {
    return !this.platform.is('cordova');
  }

  async updateAllowed() {
    const platform = Capacitor.getPlatform();
    const allowed = platform !== 'web' ? await this.ipApiService.isMx() : true;

    if (allowed !== null) this.isAllowed = allowed;
    else this.isAllowed = await this.locationS.isMx();
  }

  async login() {
    this.isLoading = true;

    this.error = '';
    const loading = await this.loadingController.create({
      message: this.translateService.instant('loading'),
    });
    await loading.present();

    const { password, username: email, remember } = this.loginForm.value;

    try {
      const platform = Capacitor.getPlatform();
      const loginOK = await this.authService.logInEmail(email, password, remember);

      log.info('login status', loginOK);

      // Si el email no está verificado, el alerta ya se mostró en AuthService, solo cerrar loading
      if (loginOK === 'EMAIL_NOT_VERIFIED') {
        await loading.dismiss();
        this.isLoading = false;
        return;
      }

      if (!loginOK) {
        // Si logInEmail devuelve false, tratamos como error genérico
        this.error = 'Correo o contraseña incorrectos.';
        await this.customAlert('Error', this.error);
        await loading.dismiss();
        this.isLoading = false;
        return;
      }

      // Obtenemos usuarios bloqueados
      this.blackUser = (await this.customerS.getBlackUsers()) || [];
      const isBlackUser = this.blackUser.some((u) => u.blackEmail === email);

      if (isBlackUser) {
        this.error = 'No es posible iniciar sesión con este correo electrónico.';
        await this.customAlert('Error', this.error);
        return;
      }

      // Validación de país permitido
      await this.updateAllowed();
      if (!this.isAllowed && platform !== 'web') {
        this.error = 'Solo se permite iniciar sesión desde México.';
        await this.customAlert('Acceso restringido', this.error);
        return;
      }

      // Asegurar token de notificaciones
      await this.notificationsS.ensurePushTokenExists();

      // Redirigimos a home
      this.router.navigate(['/home'], { replaceUrl: true, queryParamsHandling: 'preserve' });
      // Registrar login exitoso solo cuando el login fue exitoso
      this.trackingS.trackEvent('login', {}, false, false);
    } catch (err: any) {
      this.error = '';

      log.error('Error en login:', err);

      const claims = await this.authService.checkUserClaims(email);
      if (claims.migrate && !claims.password) {
        // Cerrar loading antes de abrir el modal de recovery
        await loading.dismiss();
        this.isLoading = false;
        this.openRecovery('migrate');
        return;
      } else {
        const code = err?.code || 'unknown';
        this.error = this.getFirebaseErrorMessage(code);
      }
    } finally {
      // Solo cerrar loading si no se cerró antes (en caso de migración)
      if (this.isLoading) {
        await loading.dismiss();
        this.isLoading = false;
      }
    }
  }

  getFirebaseErrorMessage(code: string): string {
    const errorMap: { [key: string]: string } = {
      'auth/invalid-email': 'El correo electrónico no es válido.',
      'auth/user-disabled': 'Esta cuenta ha sido deshabilitada ya que se solicito el proceso de eliminación.',
      'auth/user-not-found': 'No existe una cuenta con este correo electrónico.',
      'auth/wrong-password': 'La contraseña es incorrecta.',
      'auth/too-many-requests': 'Demasiados intentos fallidos. Inténtalo más tarde.',
      'auth/network-request-failed': 'Error de conexión. Verifica tu red e inténtalo nuevamente.',
      'auth/invalid-credential': 'Las credenciales no son correctas, revisa e inténtalo de nuevo.',
      'auth/popup-closed-by-user': 'Inicio de sesión cancelado.',
      'auth/missing-email': 'Por favor, ingresa un correo electrónico.',
      'auth/internal-error': 'Ocurrió un error interno. Intenta de nuevo más tarde.',
      'auth/invalid-login-credentials': 'El correo o contraseña no son válidos.',
      unknown: 'Error desconocido al iniciar sesión.',
    };

    return errorMap[code] || 'Error al iniciar sesión. Por favor, inténtalo de nuevo.';
  }

  async openRecovery(status: string) {
    const modal = await this.modalCtrl.create({
      component: RecoveryComponent,
      componentProps: {
        email: this.loginForm.value.username ?? '',
        status,
      },
    });
    await modal.present();
  }

  async customAlert(header: string, message: string) {
    const alert = await this.alertCtrl.create({
      header,
      message,
      buttons: ['Continuar'],
      backdropDismiss: false,
    });
    await alert.present();
  }
}
